/**
 * Application shell (APP-001 §14).
 *
 * The permanent layout: skip link → Sidebar (desktop) / TopBar (mobile) →
 * main content region. The main region carries id="main-content" as the skip
 * link target and a semantic <main> landmark.
 *
 * `variant="focus"` renders the distraction-free shell used while onboarding
 * is incomplete (APP-002 §30): no primary navigation, so no link can navigate
 * out of the onboarding trap mid-flow (the gate would bounce the user back to
 * /onboarding with all in-component step state reset). The identity context
 * and main landmark are identical in both variants.
 */
import type { ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { SkipLink } from './SkipLink';
import type { DashboardIdentityResponse } from '@/api/dashboard-types';

export interface AppShellProps {
  identity: DashboardIdentityResponse;
  children: ReactNode;
  /** 'app' (default) renders the full navigation shell; 'focus' hides it. */
  variant?: 'app' | 'focus';
}

export function AppShell({ identity, children, variant = 'app' }: AppShellProps) {
  if (variant === 'focus') {
    return (
      <div className="flex h-full min-h-screen flex-col">
        <SkipLink />
        <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto focus:outline-none">
          {children}
        </main>
      </div>
    );
  }
  return (
    <div className="flex h-full min-h-screen flex-col">
      <SkipLink />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-hidden">
          <TopBar identity={identity} />
          <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto focus:outline-none">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
